import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  ChevronLeft,
  Filter,
  Search,
  CheckCircle2,
  Timer,
  XCircle,
  Eye,
  Download,
  TrendingUp,
  Ticket,
  DollarSign,
  Sparkles,
  ArrowRight,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import Footer from "../../components/landing/Footer";

const STATUS_CONFIG = {
  confirmed: {
    label: "Confirmed",
    icon: CheckCircle2,
    color: "green",
    bgClass: "bg-green-50",
    textClass: "text-green-700",
    borderClass: "border-green-300",
    iconClass: "text-green-600",
  },
  pending: {
    label: "Pending",
    icon: Timer,
    color: "amber",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
    borderClass: "border-amber-300",
    iconClass: "text-amber-600",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    color: "red",
    bgClass: "bg-red-50",
    textClass: "text-red-700",
    borderClass: "border-red-300",
    iconClass: "text-red-600",
  },
};

export default function MyEventRegistrations() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
    totalSpent: 0,
  });

  const calculateStats = useCallback((events) => {
    // Filter out conferences and trade missions
    const generalEvents = events.filter((e) => {
      const eventTypes = e.event?.event_type || [];
      return !eventTypes.includes("conference") && !eventTypes.includes("trade_mission");
    });
    
    const stats = {
      total: generalEvents.length,
      confirmed: generalEvents.filter((e) => e.status === "confirmed" || e.status === "approved").length,
      pending: generalEvents.filter((e) => e.status === "pending" || !e.status).length,
      totalSpent: generalEvents
        .filter((e) => e.status === "confirmed" || e.status === "approved")
        .reduce((sum, e) => sum + (parseFloat(e.amount_paid) || 0), 0),
    };
    setStats(stats);
  }, []);

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await conferenceAndTradeMissionService.getAllRegisteredEvents("others");

      if (response.data.status_code === 200) {
        const events = response.data.data || [];
        setRegistrations(events);
        calculateStats(events);
      } else {
        toast.error(response.data.reason || "Failed to fetch registrations");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.reason || "Failed to fetch registrations"
      );
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  const applyFilters = useCallback(() => {
    let filtered = [...registrations];

    if (searchQuery) {
      filtered = filtered.filter(
        (reg) =>
          reg.event?.event_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          reg.booking_id?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (reg) => (reg.status || "pending") === statusFilter
      );
    }

    setFilteredRegistrations(filtered);
  }, [registrations, searchQuery, statusFilter]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const getStatusConfig = (status) => {
    if (status === "approved") return STATUS_CONFIG.confirmed;
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  const handleViewDetails = (registration) => {
    if (registration.event?.slug) {
      navigate(`/event-details/${registration.event.slug}`);
    }
  };

  const handleDownloadTicket = (registration) => {
    toast.info("Download feature coming soon");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-emerald-500/[0.08] via-teal-500/[0.06] to-transparent" aria-hidden />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
              <Ticket size={20} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="mb-0.5 flex items-center justify-start">
                <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                  My bookings
                </span>
              </div>
              <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
                My Event Bookings
              </h1>
              <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm">
                Manage your event tickets and bookings
              </p>
            </div>
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
            <motion.button
              type="button"
              onClick={() => navigate(-1)}
              whileHover={{ x: -2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-emerald-500/10 hover:text-emerald-700 sm:flex-none"
            >
              <ChevronLeft size={20} strokeWidth={2} />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">Back</span>
            </motion.button>
            <motion.button
              type="button"
              onClick={() => navigate("/events")}
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-semibold text-emerald-700 shadow-sm transition-colors hover:bg-emerald-500/10 hover:text-emerald-800 sm:flex-none"
            >
              <Sparkles size={18} />
              <span className="sm:hidden">Browse</span>
              <span className="hidden sm:inline">Browse Events</span>
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </div>
      </header>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="grid grid-cols-2 gap-4 mb-8 md:grid-cols-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative overflow-hidden p-6 bg-gradient-to-br from-purple-50 to-white rounded-2xl border-2 border-purple-200 shadow-lg hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-200/20 rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Total
                  </p>
                  <div className="p-2 bg-purple-100 rounded-lg group-hover:scale-110 transition-transform">
                    <TrendingUp size={18} className="text-purple-600" />
                  </div>
                </div>
                <p className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-purple-800">
                  {stats.total}
                </p>
                <p className="mt-1 text-xs text-purple-600">Total bookings</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25 }}
              className="relative overflow-hidden p-6 bg-gradient-to-br from-green-50 to-white rounded-2xl border-2 border-green-200 shadow-lg hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-green-200/20 rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Confirmed
                  </p>
                  <div className="p-2 bg-green-100 rounded-lg group-hover:scale-110 transition-transform">
                    <CheckCircle2 size={18} className="text-green-600" />
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-green-800">
                  {stats.confirmed}
                </p>
                <p className="mt-1 text-xs text-green-600">Ready to attend</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="relative overflow-hidden p-6 bg-gradient-to-br from-amber-50 to-white rounded-2xl border-2 border-amber-200 shadow-lg hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-amber-200/20 rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Pending
                  </p>
                  <div className="p-2 bg-amber-100 rounded-lg group-hover:scale-110 transition-transform">
                    <Timer size={18} className="text-amber-600" />
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-amber-800">
                  {stats.pending}
                </p>
                <p className="mt-1 text-xs text-amber-600">Awaiting confirmation</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.35 }}
              className="relative overflow-hidden p-6 bg-gradient-to-br from-blue-50 to-white rounded-2xl border-2 border-blue-200 shadow-lg hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-200/20 rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Total Spent
                  </p>
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:scale-110 transition-transform">
                    <DollarSign size={18} className="text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">
                  GH₵{stats.totalSpent.toFixed(2)}
                </p>
                <p className="mt-1 text-xs text-blue-600">Across all events</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Search and Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="p-6 mb-8 bg-white rounded-2xl border border-gray-200 shadow-lg"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search
                  className="absolute top-3.5 left-4 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by event name or booking ID..."
                  className="py-3 pr-4 pl-12 w-full text-sm text-gray-900 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white"
                />
              </div>

              <div className="flex gap-2 items-center">
                <Filter size={20} className="text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-5 py-3 text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all outline-none hover:bg-gray-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="all">All Status</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Registrations List */}
          {loading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full border-4 border-emerald-200 animate-spin border-t-emerald-500" />
                <div className="flex absolute inset-0 justify-center items-center">
                  <Ticket size={32} className="text-emerald-500" />
                </div>
              </div>
              <p className="text-lg font-semibold text-gray-600">Loading your bookings...</p>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-20 text-center"
            >
              <div className="flex justify-center mb-6">
                <div className="flex justify-center items-center w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-200 rounded-full">
                  <Ticket size={48} className="text-emerald-600" />
                </div>
              </div>
              <h3 className="mb-3 text-2xl font-bold text-gray-900">
                {searchQuery || statusFilter !== "all"
                  ? "No results found"
                  : "No event bookings yet"}
              </h3>
              <p className="mb-8 text-base text-gray-600 max-w-md mx-auto">
                {searchQuery || statusFilter !== "all"
                  ? "Try adjusting your filters or search query"
                  : "Start by booking tickets for an exciting event"}
              </p>
              {!searchQuery && statusFilter === "all" && (
                <motion.button
                  onClick={() => navigate("/events")}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex gap-2 items-center px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-xl transition-all hover:from-emerald-600 hover:to-teal-700 hover:shadow-2xl"
                >
                  <Sparkles size={20} />
                  Browse Events
                  <ArrowRight size={18} />
                </motion.button>
              )}
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredRegistrations.map((registration, index) => {
                  const status = registration.status || "pending";
                  const statusConfig = getStatusConfig(status);
                  const StatusIcon = statusConfig.icon;
                  const eventData = registration.event || {};

                  return (
                    <motion.div
                      key={registration.id || index}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex overflow-hidden flex-col bg-white rounded-2xl border-2 border-gray-200 shadow-lg transition-all hover:border-emerald-300 hover:shadow-2xl group"
                    >
                      {/* Event Image */}
                      <div className="relative h-48 bg-gradient-to-br from-emerald-100 via-teal-100 to-pink-100">
                        {eventData.thumbnail && (
                          <img
                            src={eventData.thumbnail}
                            alt={eventData.event_title}
                            className="object-cover w-full h-full transition-transform group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/50" />

                        {/* Status Badge */}
                        <div className="absolute top-3 right-3">
                          <div
                            className={`flex gap-2 items-center px-3 py-1.5 text-xs font-bold backdrop-blur-md rounded-full border-2 ${statusConfig.bgClass} ${statusConfig.textClass} ${statusConfig.borderClass} shadow-lg`}
                          >
                            <StatusIcon size={14} />
                            <span>{statusConfig.label}</span>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex flex-col flex-1 p-6">
                        <h3 className="mb-3 text-lg font-bold leading-tight text-gray-900 transition-colors line-clamp-2 group-hover:text-emerald-600">
                          {eventData.event_title || "Untitled Event"}
                        </h3>

                        <div className="flex-1 mb-4 space-y-3">
                          {/* Date & Time */}
                          {((eventData.event_start_date || eventData.event_date) || (eventData.event_end_date || eventData.event_date)) && (
                            <div className="flex flex-wrap gap-2 items-center text-sm text-gray-700">
                              <div className="flex gap-2 items-center">
                                <div className="p-1.5 bg-emerald-100 rounded-lg">
                                  <Calendar size={14} className="text-emerald-600" />
                                </div>
                                <span className="font-medium">
                                  {(() => {
                                    const start = eventData.event_start_date || eventData.event_date;
                                    const end = eventData.event_end_date || eventData.event_date;
                                    return start && end && start !== end
                                      ? `${formatDate(start)} – ${formatDate(end)}`
                                      : formatDate(start || eventData.event_date);
                                  })()}
                                </span>
                              </div>
                              {eventData.event_time && (
                                <>
                                  <span className="text-gray-400">•</span>
                                  <div className="flex gap-2 items-center">
                                    <div className="p-1.5 bg-emerald-100 rounded-lg">
                                      <Clock size={14} className="text-emerald-600" />
                                    </div>
                                    <span className="font-medium">
                                      {formatTime(eventData.event_time)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          {/* Venue */}
                          {eventData.venue_name && (
                            <div className="flex gap-2 items-start">
                              <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                                <MapPin size={14} className="text-blue-600" />
                              </div>
                              <span className="text-sm text-gray-700 font-medium line-clamp-2">
                                {eventData.venue_name}
                              </span>
                            </div>
                          )}

                          {/* Booking Info */}
                          {registration.booking_id && (
                            <div className="pt-3 mt-3 border-t border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                Booking ID
                              </p>
                              <p className="font-mono text-sm font-bold text-gray-900">
                                {registration.booking_id}
                              </p>
                            </div>
                          )}

                          {/* Tickets Info */}
                          {registration.quantity && (
                            <div className="flex gap-2 items-center text-sm">
                              <Users size={16} className="text-emerald-600" />
                              <span className="text-gray-700 font-medium">
                                {registration.quantity} {registration.quantity > 1 ? "Tickets" : "Ticket"}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <motion.button
                            onClick={() => handleViewDetails(registration)}
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex flex-1 gap-2 justify-center items-center px-4 py-2.5 text-sm font-bold text-gray-700 bg-gray-100 rounded-xl border-2 border-gray-200 transition-all hover:bg-gray-200 hover:border-gray-300"
                          >
                            <Eye size={16} />
                            View Event
                          </motion.button>

                          {(status === "confirmed" || status === "approved") && (
                            <motion.button
                              onClick={() => handleDownloadTicket(registration)}
                              whileHover={{ scale: 1.02, y: -2 }}
                              whileTap={{ scale: 0.98 }}
                              className="flex flex-1 gap-2 justify-center items-center px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-lg transition-all hover:from-emerald-600 hover:to-teal-700 hover:shadow-xl"
                            >
                              <Download size={16} />
                              Ticket
                            </motion.button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </AnimatePresence>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

